import React, { useState } from 'react';
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    SafeAreaView,
    KeyboardAvoidingView,
    Platform,
    Image,
    ActivityIndicator,
    Modal
} from 'react-native';
import { Link, useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Mail, ChevronLeft, CheckCircle, AlertCircle } from 'lucide-react-native';

export default function ForgotPasswordScreen() {
    const router = useRouter();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const { forgotPassword } = useAuth();
    const { colors } = useTheme();

    // Modal State
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState('');

    const showErrorModal = (message: string) => {
        setErrorModalMessage(message);
        setErrorModalVisible(true);
    };

    const handleResetPassword = async () => {
        if (!email) {
            showErrorModal('Por favor, insira seu e-mail.');
            return;
        }

        // Validação básica de email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showErrorModal('Por favor, insira um e-mail válido.');
            return;
        }

        setLoading(true);
        try {
            await forgotPassword(email);
            setSuccess(true);
        } catch (error: any) {
            showErrorModal(error.message || 'Erro ao enviar e-mail de recuperação.');
        } finally {
            setLoading(false);
        }
    };

    const styles = StyleSheet.create({
        container: {
            flex: 1,
            backgroundColor: colors.background,
        },
        backButton: {
            position: 'absolute',
            top: 24,
            left: 24,
            padding: 12,
            borderRadius: 24,
            zIndex: 10,
        },
        keyboardView: {
            flex: 1,
        },
        content: {
            flex: 1,
            paddingHorizontal: 24,
            justifyContent: 'center',
        },
        header: {
            alignItems: 'center',
            marginBottom: 48,
        },
        logo: {
            width: 80,
            height: 80,
            marginBottom: 16,
        },
        title: {
            fontSize: 28,
            fontFamily: 'Inter-Bold',
            color: colors.text,
            marginBottom: 8,
        },
        subtitle: {
            fontSize: 16,
            fontFamily: 'Inter-Regular',
            color: colors.textSecondary,
            textAlign: 'center',
            lineHeight: 24,
            paddingHorizontal: 16,
        },
        form: {
            gap: 16,
        },
        inputContainer: {
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: colors.surface,
            borderRadius: 12,
            paddingHorizontal: 16,
            height: 56,
            borderWidth: 1,
            borderColor: colors.border,
        },
        inputIcon: {
            marginRight: 12,
        },
        input: {
            flex: 1,
            fontSize: 16,
            fontFamily: 'Inter-Regular',
            color: colors.text,
        },
        submitButton: {
            backgroundColor: colors.primary,
            borderRadius: 12,
            height: 56,
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 8,
        },
        submitButtonDisabled: {
            opacity: 0.6,
        },
        submitButtonText: {
            fontSize: 16,
            fontFamily: 'Inter-SemiBold',
            color: '#FFFFFF',
        },
        backToLoginContainer: {
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 24,
        },
        backToLoginText: {
            fontSize: 14,
            fontFamily: 'Inter-Regular',
            color: colors.textSecondary,
        },
        backToLoginLink: {
            fontSize: 14,
            fontFamily: 'Inter-SemiBold',
            color: colors.primary,
        },
        // Success State
        successContainer: {
            alignItems: 'center',
            gap: 16,
        },
        successIcon: {
            marginBottom: 8,
        },
        successTitle: {
            fontSize: 24,
            fontFamily: 'Inter-Bold',
            color: colors.text,
            textAlign: 'center',
        },
        successMessage: {
            fontSize: 16,
            fontFamily: 'Inter-Regular',
            color: colors.textSecondary,
            textAlign: 'center',
            lineHeight: 24,
            paddingHorizontal: 16,
        },
        goToLoginButton: {
            backgroundColor: colors.primary,
            borderRadius: 12,
            height: 56,
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            marginTop: 16,
        },
        // Modal Styles
        modalOverlay: {
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 24,
        },
        modalContent: {
            width: '100%',
            maxWidth: 320,
            borderRadius: 24,
            padding: 24,
            alignItems: 'center',
        },
        modalIconContainer: {
            marginBottom: 16,
        },
        modalTitle: {
            fontSize: 20,
            fontFamily: 'Inter-Bold',
            marginBottom: 8,
            textAlign: 'center',
        },
        modalMessage: {
            fontSize: 14,
            fontFamily: 'Inter-Regular',
            textAlign: 'center',
            marginBottom: 24,
            lineHeight: 20,
        },
        modalButton: {
            width: '100%',
            paddingVertical: 12,
            borderRadius: 12,
            alignItems: 'center',
        },
        modalButtonText: {
            fontSize: 16,
            fontFamily: 'Inter-SemiBold',
            color: '#FFFFFF',
        },
    });

    if (success) {
        return (
            <SafeAreaView style={styles.container}>
                <View style={styles.content}>
                    <View style={styles.successContainer}>
                        <CheckCircle size={64} color={colors.success} style={styles.successIcon} />
                        <Text style={styles.successTitle}>E-mail Enviado!</Text>
                        <Text style={styles.successMessage}>
                            Enviamos um link de recuperação para {email}.
                            Verifique sua caixa de entrada e spam.
                        </Text>
                        <TouchableOpacity
                            style={styles.goToLoginButton}
                            onPress={() => router.push('/(auth)/login')}
                        >
                            <Text style={styles.submitButtonText}>Voltar para Login</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.container}>
            <TouchableOpacity
                style={[styles.backButton, { backgroundColor: colors.surface }]}
                activeOpacity={0.7}
                onPress={() => router.back()}
            >
                <ChevronLeft size={20} color={colors.text} />
            </TouchableOpacity>
            <KeyboardAvoidingView
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                style={styles.keyboardView}
            >
                <View style={styles.content}>
                    <View style={styles.header}>
                        <Image
                            source={require('@/public/icon-pages.png')}
                            style={styles.logo}
                            resizeMode="contain"
                        />
                        <Text style={styles.title}>Esqueci Minha Senha</Text>
                        <Text style={styles.subtitle}>
                            Digite seu e-mail e enviaremos um link para redefinir sua senha.
                        </Text>
                    </View>

                    <View style={styles.form}>
                        <View style={styles.inputContainer}>
                            <Mail size={20} color={colors.textSecondary} style={styles.inputIcon} />
                            <TextInput
                                style={styles.input}
                                placeholder="Email"
                                placeholderTextColor={colors.textSecondary}
                                value={email}
                                onChangeText={setEmail}
                                keyboardType="email-address"
                                autoCapitalize="none"
                                autoFocus
                            />
                        </View>

                        <TouchableOpacity
                            style={[styles.submitButton, loading && styles.submitButtonDisabled]}
                            onPress={handleResetPassword}
                            disabled={loading}
                        >
                            {loading ? (
                                <ActivityIndicator color="#FFFFFF" />
                            ) : (
                                <Text style={styles.submitButtonText}>Enviar Link de Recuperação</Text>
                            )}
                        </TouchableOpacity>

                        <View style={styles.backToLoginContainer}>
                            <Text style={styles.backToLoginText}>Lembrou a senha? </Text>
                            <Link href="/(auth)/login" asChild>
                                <TouchableOpacity>
                                    <Text style={styles.backToLoginLink}>Fazer Login</Text>
                                </TouchableOpacity>
                            </Link>
                        </View>
                    </View>
                </View>
            </KeyboardAvoidingView>

            {/* Error Modal */}
            <Modal
                animationType="fade"
                transparent={true}
                visible={errorModalVisible}
                onRequestClose={() => setErrorModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={[styles.modalContent, { backgroundColor: colors.surface }]}>
                        <View style={styles.modalIconContainer}>
                            <AlertCircle size={48} color={colors.error} />
                        </View>
                        <Text style={[styles.modalTitle, { color: colors.text }]}>Erro</Text>
                        <Text style={[styles.modalMessage, { color: colors.textSecondary }]}>
                            {errorModalMessage}
                        </Text>
                        <TouchableOpacity
                            style={[styles.modalButton, { backgroundColor: colors.primary }]}
                            onPress={() => setErrorModalVisible(false)}
                        >
                            <Text style={styles.modalButtonText}>OK</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </Modal>
        </SafeAreaView>
    );
}
